import React from 'react'
import { ReactComponent as EditContent } from '../../assets/svg/EditContent.svg'
import { ReactComponent as DeleteContent } from '../../assets/svg/DeleteContent.svg'
import { format_day, notify, translateTargetText, getContentRowIcon } from '../../utils/util'
import { fileSystemUrl } from '../../constants/Connections'
import { useUser } from '../../auth'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { approveContentAsync, sendNotification } from '../../server/server'
import { useSocket } from '../../context/socket'
import ProgramInfo from '../ProgramInfo'
import { STAGGER_CAP } from '../content/ToGo/ContentCard'

const PREVIEW_WIDTH = 180

/**
 * One row in the upcoming / review content panels. Memoized: the panel
 * re-renders while paging and dimming, and only new rows should mount.
 * `index` drives the staggered entrance (see .dsh-list-animate).
 */
const UpcomingSingleContent = React.memo(function UpcomingSingleContent({ content, index = 0, onDelete, onOpenImage, reviewContent = false, onReviewed }) {
    const { user } = useUser()
    const { socket } = useSocket()
    const history = useHistory()
    const { t, i18n } = useTranslation()

    const imageUrl = content?.files ? `${fileSystemUrl}/${content.files.storage_name}?token=${user?.token}` : null

    const handleEdit = (e) => {
        e.stopPropagation()
        history.push(`/content?editId=${content?.id}`)
    }

    const approve = async (approved) => {
        const resp = await approveContentAsync({ content_id: content.id, approved })
        if (resp?.status === 200) {
            const reviewed = resp.data
            socket?.emit('new_content', reviewed)
            sendNotification({ content: reviewed, type: 'task' })
            sendNotification({ content: reviewed, type: 'reviewed' })
            notify(t(approved ? 'Content has been approved' : 'Content has been declined'))
            onReviewed?.()
        }
    }

    return (
        <div className="dsh-row bg-containerGray rounded-xl mb-4 p-3 mx-8" style={{ '--i': Math.min(index, STAGGER_CAP) }}>
            <div className="header-single-content flex flex-row justify-between" style={{ alignItems: 'start' }}>
                <div className="flex flex-row items-center gap-2">
                    {getContentRowIcon(content?.type)}
                    <span className="text-mudPurple font-medium text-sm">
                        {t('Open')} {t(content?.type)}
                    </span>
                </div>
                <ProgramInfo programInfo={content?.program_info} className="text-center flex-1 mx-3" />

                {!reviewContent && (
                    <div className="flex flex-row items-center gap-3">
                        {content?.type !== 'journal' && <EditContent id="edit-upcoming-content-button" style={{ cursor: 'pointer' }} fill="#BEBECE" onClick={handleEdit} />}
                        <DeleteContent
                            className="cursor-pointer"
                            id="delete-upcoming-content-button"
                            fill="#FF747F"
                            onClick={(e) => {
                                e.stopPropagation()
                                onDelete(content)
                            }}
                        />
                    </div>
                )}
                {reviewContent && (
                    <span className="text-[#9392ad99] text-sm">
                        {t('by')}: {content?.created_by?.first_name} {content?.created_by?.last_name}
                    </span>
                )}
            </div>
            {imageUrl && (
                <div className="mr-3 my-2 cursor-pointer" onClick={() => onOpenImage(imageUrl)}>
                    <div className="image-container">
                        <img src={`${imageUrl}&width=${PREVIEW_WIDTH}`} alt="Task" className="task-image" style={{ width: PREVIEW_WIDTH }} />
                    </div>
                </div>
            )}
            <div className="text-deepPurple mt-3 font-bold text-lg text-left max-w-xs">{content?.title}</div>
            {!!content?.repeat_id && (
                <div className="mt-2 text-left">
                    <span id="upcoming-series-badge" className="inline-block rounded-full bg-lightBG text-deepPurple text-xs font-semibold px-3 py-1">
                        {content?.repeatPeriodText ? `${content.repeatPeriodText} · ` : ''}
                        {t('scheduledRuns', { count: content?.series_count || 1 })}
                    </span>
                </div>
            )}
            <p className="text-sm text-deepPurple text-left mt-3">{content?.description}</p>
            {content?.type === 'poll' && Array.isArray(content?.options) && (
                <div className="mt-3 text-left">
                    <span className="text-[12px] text-mudPurple mb-2">{t('options')} </span>
                    {content.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="text-[16px] text-mudPurple bg-white rounded-xl w-full py-[0.6rem] mb-2 text-left px-3">
                            {option}
                        </div>
                    ))}
                </div>
            )}
            {reviewContent && (
                <div className="flex flex-row gap-[10px]">
                    <button
                        type="button"
                        className="h-[50px] border border-[#BEBECE] flex-1 rounded-[10px] bg-[#FAFAFF] text-[#9392AD] text-[16px] font-semibold transition hover:bg-[#BEBECE] hover:text-[white]"
                        onClick={() => approve(false)}
                    >
                        {t('Decline')}
                    </button>
                    <button
                        type="button"
                        className="h-[50px] border border-[#5E639B] flex-1 rounded-[10px] bg-[#5E639B] text-[#FDDF6D] text-[16px] font-semibold transition hover:bg-[#FDDF6D] hover:text-[#5E639B] hover:border-[#FDDF6D]"
                        onClick={() => approve(true)}
                    >
                        {t('Accept')}
                    </button>
                </div>
            )}
            <div className="flex justify-between items-center mt-3">
                <span className="text-mudPurple font-medium text-sm">
                    <span className="text-[#9392ad99] text-sm">{t('target')} :</span> {translateTargetText(content?.target_text)}
                </span>
                <p>
                    <span className="text-[#9392ad99] text-sm">
                        {t('date')} & {t('time')}:{' '}
                    </span>
                    <span className="text-mudPurple text-sm">{format_day(content?.publicationDate, true, i18n.language)}</span>
                </p>
            </div>
        </div>
    )
})

export default UpcomingSingleContent
